import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, addDays, addMinutes } from "date-fns";
import { sv } from "date-fns/locale";
// UPPDRAG-30: lifted from a require() inside useMemo to a top-level ES import.
// Path B+ (Lesson 42): bytte calculateBookingPrice('standard') →
// resolveBookingTargetPrice (per-seat-formel även för move-flow). M3-fix.
import {
  resolveBookingTargetPrice,
  bookingKindForPricing,
  type PricingRule,
} from "@/lib/pricing";
import { MapPin, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getLocalDayBounds } from "@/lib/utils";
import PriceChangeIndicator from "./PriceChangeIndicator";
import { useTranslation } from "react-i18next";

function buildRecurringSyntheticSlots(
  recurringBookings: any[],
  recurringBlocks: any[],
  dateStr: string,
): { court_id: string; start_time: string; end_time: string }[] {
  const synth: { court_id: string; start_time: string; end_time: string }[] = [];
  for (const rb of recurringBookings) {
    if (rb.skipped_dates?.includes(dateStr)) continue;
    if (rb.interval_weeks && rb.interval_weeks > 1 && rb.valid_from) {
      const diffMs = new Date(dateStr).getTime() - new Date(rb.valid_from).getTime();
      if (Math.round(diffMs / (7 * 24 * 60 * 60 * 1000)) % rb.interval_weeks !== 0) continue;
    }
    synth.push({ court_id: rb.court_id, start_time: `${dateStr}T${rb.start_time}`, end_time: `${dateStr}T${rb.end_time}` });
  }
  for (const block of recurringBlocks) {
    if (block.skipped_dates?.includes(dateStr)) continue;
    for (const courtId of block.court_ids ?? []) {
      synth.push({ court_id: courtId, start_time: `${dateStr}T${block.start_time}`, end_time: `${dateStr}T${block.end_time}` });
    }
  }
  return synth;
}

interface Props {
  open: boolean;
  onClose: () => void;
  booking: any;
  hallId: string;
}

export default function RescheduleDialog({ open, onClose, booking, hallId }: Props) {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<{ courtId: string; courtName: string; startTime: Date } | null>(null);

  const dates = useMemo(() => Array.from({ length: 14 }, (_, i) => addDays(new Date(), i)), []);

  const { data: courts = [] } = useQuery({
    queryKey: ["reschedule-courts", hallId],
    queryFn: async () => {
      const { data } = await supabase.from("courts").select("id, name").eq("hall_id", hallId).eq("status", "active").order("sort_order");
      return data ?? [];
    },
    enabled: open && !!hallId,
  });

  const reschDateStr = format(selectedDate, "yyyy-MM-dd");
  const reschDow = selectedDate.getDay();

  const { data: existingBookingsRaw = [] } = useQuery({
    queryKey: ["reschedule-existing", hallId, reschDateStr],
    queryFn: async () => {
      const { start, end } = getLocalDayBounds(selectedDate);
      const { data } = await supabase
        .from("bookings")
        .select("court_id, start_time, end_time")
        .not("status", "in", "(cancelled)")
        .gte("start_time", start)
        .lte("start_time", end)
        .is("deleted_at", null)
        .neq("id", booking.id);
      return data ?? [];
    },
    enabled: open,
  });

  const { data: reschRecurringBookings = [] } = useQuery({
    queryKey: ["reschedule-recurring", organization?.id, reschDateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_bookings")
        .select("id, court_id, start_time, end_time, skipped_dates, interval_weeks, valid_from")
        .eq("organization_id", organization!.id)
        .eq("day_of_week", reschDow)
        .lte("valid_from", reschDateStr)
        .gte("valid_until", reschDateStr)
        .eq("status", "active");
      return data ?? [];
    },
    enabled: open && !!organization?.id,
  });

  const { data: reschRecurringBlocks = [] } = useQuery({
    queryKey: ["reschedule-blocks", organization?.id, reschDateStr],
    queryFn: async () => {
      const { data } = await supabase
        .from("recurring_blocks" as any)
        .select("id, court_ids, start_time, end_time, skipped_dates, interval_weeks, valid_from, valid_until, day_of_week")
        .eq("organization_id", organization!.id)
        .eq("day_of_week", reschDow)
        .lte("valid_from", reschDateStr)
        .eq("status", "active");
      return (data ?? []).filter((b: any) => !b.valid_until || b.valid_until >= reschDateStr);
    },
    enabled: open && !!organization?.id,
  });

  const existingBookings = useMemo(
    () => [...existingBookingsRaw, ...buildRecurringSyntheticSlots(reschRecurringBookings, reschRecurringBlocks, reschDateStr)],
    [existingBookingsRaw, reschRecurringBookings, reschRecurringBlocks, reschDateStr],
  );

  const { data: hallData } = useQuery({
    queryKey: ["reschedule-hall", hallId],
    queryFn: async () => {
      const { data } = await supabase.from("halls").select("opening_time, closing_time").eq("id", hallId).single();
      return data;
    },
    enabled: open && !!hallId,
  });

  const { data: pricingRules = [] } = useQuery<any[]>({
    queryKey: ["reschedule-pricing", organization?.id],
    queryFn: async () => {
      const { data } = await (supabase as any).from("pricing_rules").select("*").eq("organization_id", organization!.id);
      return data ?? [];
    },
    enabled: open && !!organization?.id,
  });

  const availableSlots = useMemo(() => {
    if (!hallData || courts.length === 0) return [];
    const openH = parseInt(hallData.opening_time?.split(":")[0] ?? "7");
    const closeH = parseInt(hallData.closing_time?.split(":")[0] ?? "23") || 24;
    const duration = booking.duration_minutes;
    const slots: { courtId: string; courtName: string; startTime: Date }[] = [];

    for (let h = openH; h < closeH; h++) {
      for (const m of [0, 30]) {
        const slotStart = new Date(selectedDate);
        slotStart.setHours(h, m, 0, 0);
        const slotEnd = addMinutes(slotStart, duration);
        if (slotEnd.getHours() > closeH || (slotEnd.getHours() === closeH && slotEnd.getMinutes() > 0)) continue;
        if (slotStart <= new Date()) continue;

        for (const court of courts) {
          const hasConflict = existingBookings.some((b: any) => {
            if (b.court_id !== court.id) return false;
            const bStart = new Date(b.start_time);
            const bEnd = new Date(b.end_time);
            return slotStart < bEnd && slotEnd > bStart;
          });
          if (!hasConflict) {
            slots.push({ courtId: court.id, courtName: court.name, startTime: slotStart });
          }
        }
      }
    }
    return slots;
  }, [hallData, courts, existingBookings, selectedDate, booking.duration_minutes]);

  const slotsByTime = useMemo(() => {
    const map = new Map<string, typeof availableSlots>();
    availableSlots.forEach((s) => {
      const key = format(s.startTime, "HH:mm");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    });
    return map;
  }, [availableSlots]);

  // Path B+ — booker + invited categories från booking_players-snapshot
  // (samma mönster som MoveBookingDialog för konsistens). Resolverar pure-
  // helper-input för per-seat-formeln. Fallback 'standard' om snapshot
  // saknas (legacy bokningar pre-booking_players-rad-för-bokaren).
  const bookingKindReschedule = useMemo(
    () => bookingKindForPricing({
      is_open_match: booking.is_open_match ?? false,
      activity_id: booking.activity_id ?? null,
    }),
    [booking.is_open_match, booking.activity_id],
  );
  const isPrivateReschedule = bookingKindReschedule === "private";

  const { data: reschBookingPlayers = [] } = useQuery({
    queryKey: ["reschedule-booking-players", booking.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_players")
        .select("user_id, category_slug")
        .eq("booking_id", booking.id);
      if (error) throw error;
      return (data ?? []).map((p) => ({
        user_id: (p as { user_id: string }).user_id,
        category_slug:
          (p as { category_slug: string | null }).category_slug ?? "standard",
      }));
    },
    enabled: open && isPrivateReschedule,
    staleTime: 5 * 60_000,
  });

  const reschBookerCategorySlug =
    reschBookingPlayers.find((p) => p.user_id === booking.user_id)
      ?.category_slug ?? "standard";

  const reschInvitedCategorySlugs = useMemo(
    () =>
      reschBookingPlayers
        .filter((p) => p.user_id !== booking.user_id)
        .map((p) => p.category_slug),
    [reschBookingPlayers, booking.user_id],
  );

  const reschCourtSeatCount = useMemo(
    () =>
      (courts.find((c) => c.id === selectedSlot?.courtId) as any)
        ?.max_players ?? 4,
    [courts, selectedSlot?.courtId],
  );

  // Compute new price for selected slot — Path B+ canonical resolver.
  // Per-seat-formeln triggas för member-bookers; F5-guard preserverar
  // standard-only-fall (M3-fix: move-flow får samma canonical-pris som
  // create-flow, ej längre full court-pris via calculateBookingPrice).
  const newPriceTarget = useMemo(() => {
    if (!selectedSlot || pricingRules.length === 0) return null;
    try {
      return resolveBookingTargetPrice({
        bookingKind: bookingKindReschedule,
        targetSlot: {
          hallId,
          startTime: selectedSlot.startTime,
          durationMinutes: booking.duration_minutes,
          seatCount: reschCourtSeatCount,
          pricingRules: pricingRules as PricingRule[],
          specialDates: [],
        },
        bookerCategorySlug: reschBookerCategorySlug,
        invitedCategorySlugs: reschInvitedCategorySlugs,
      });
    } catch {
      return null;
    }
  }, [
    selectedSlot,
    pricingRules,
    booking.duration_minutes,
    bookingKindReschedule,
    hallId,
    reschCourtSeatCount,
    reschBookerCategorySlug,
    reschInvitedCategorySlugs,
  ]);

  // Legacy newPriceEstimate-shape för PriceChangeIndicator (UI tar { total }).
  // Path B+: total = öre/100 (öre-exakt source = newPriceTarget.totalMinor).
  const newPriceEstimate = useMemo(
    () =>
      newPriceTarget
        ? { total: Math.round(newPriceTarget.totalMinor / 100) }
        : null,
    [newPriceTarget],
  );

  const oldPrice = booking.price_total ?? 0;
  const newPrice = newPriceEstimate?.total ?? oldPrice;

  const rescheduleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedSlot) throw new Error("Välj en tid");
      const newEnd = addMinutes(selectedSlot.startTime, booking.duration_minutes);

      const { data: conflicts } = await supabase
        .from("bookings")
        .select("id")
        .eq("court_id", selectedSlot.courtId)
        .not("status", "in", "(cancelled)")
        .lt("start_time", newEnd.toISOString())
        .gt("end_time", selectedSlot.startTime.toISOString())
        .neq("id", booking.id)
        .is("deleted_at", null)
        .limit(1);

      if (conflicts && conflicts.length > 0) throw new Error("Tiden är inte längre ledig");

      // Also check recurring bookings/blocks for conflict
      const recurringConflict = existingBookings.some((b: any) => {
        if (b.court_id !== selectedSlot.courtId) return false;
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        return selectedSlot.startTime < bEnd && newEnd > bStart;
      });
      if (recurringConflict) throw new Error("Tiden är inte längre ledig");

      // Sprint D dual-write: legacy + _minor + currency. Defensive trigger
      // would auto-fill _minor anyway, but explicit dual-write is defense-in-depth.
      // Path B+: använd öre-exakt source från resolveBookingTargetPrice
      // när tillgänglig (per-seat öre-precision), annars fallback newPrice*100.
      const newPriceMinor = newPriceTarget
        ? newPriceTarget.totalMinor
        : Math.round(newPrice * 100);
      const { error } = await supabase
        .from("bookings")
        .update({
          court_id: selectedSlot.courtId,
          start_time: selectedSlot.startTime.toISOString(),
          end_time: newEnd.toISOString(),
          price_total: newPrice,                    // legacy kronor
          price_total_minor: newPriceMinor,         // öre
          currency: "SEK",
        })
        .eq("id", booking.id);
      if (error) throw error;

      // Handle price difference
      const priceDiff = newPrice - oldPrice;
      const orgId = organization?.id;
      if (orgId && priceDiff !== 0) {
        if (priceDiff < 0) {
          // Credit the player — Sprint D dual-write.
          const refundKronor = Math.abs(priceDiff);
          await supabase.from("credit_transactions").insert({
            user_id: user!.id,
            organization_id: orgId,
            amount: refundKronor,                          // legacy kronor
            amount_minor: Math.round(refundKronor * 100),  // öre
            currency: "SEK",
            type: "refund",
            description: `Ombokning: prisändring ${oldPrice} → ${newPrice} kr`,
            source: "system",
          });
        } else {
          // Create pending payment for difference — Sprint D dual-write
          await supabase.from("booking_payments").insert({
            organization_id: orgId,
            booking_id: booking.id,
            user_id: user!.id,
            amount: priceDiff,                              // legacy kronor
            amount_minor: Math.round(priceDiff * 100),      // öre
            currency: "SEK",
            status: "pending",
            payment_type: "full",
            payment_method: "pending",
          });
        }
      }

      // Log activity
      await supabase.from("booking_activity_log").insert({
        booking_id: booking.id,
        user_id: user!.id,
        action: "rescheduled",
        description: `Flyttad till ${selectedSlot.courtName} ${format(selectedSlot.startTime, "d MMM HH:mm", { locale: sv })}`,
        actor_type: "player",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-bookings"] });
      onClose();
      toast.success(t("booking:toast.bookingMoved"));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const oldTimeLabel = `${booking.courts?.name} ${format(new Date(booking.start_time), "d MMM HH:mm", { locale: sv })}`;
  const newTimeLabel = selectedSlot ? `${selectedSlot.courtName} ${format(selectedSlot.startTime, "d MMM HH:mm", { locale: sv })}` : "";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Flytta bokning</DialogTitle>
          <p className="text-xs text-muted-foreground">
            {booking.courts?.name} · {format(new Date(booking.start_time), "d MMM HH:mm", { locale: sv })} · {booking.duration_minutes} min
          </p>
        </DialogHeader>

        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {dates.map((d) => (
            <button
              key={d.toISOString()}
              className={`shrink-0 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                format(d, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              onClick={() => { setSelectedDate(d); setSelectedSlot(null); }}
            >
              <div>{format(d, "EEE", { locale: sv })}</div>
              <div className="font-bold">{format(d, "d")}</div>
            </button>
          ))}
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {slotsByTime.size === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">Inga lediga tider denna dag</p>
          ) : (
            Array.from(slotsByTime.entries()).map(([time, slots]) => (
              <div key={time} className="space-y-1">
                <p className="text-xs font-medium flex items-center gap-1"><Clock className="h-3 w-3" />{time}</p>
                <div className="flex gap-1.5 flex-wrap">
                  {slots.map((s) => {
                    const isSelected = selectedSlot?.courtId === s.courtId && selectedSlot?.startTime.getTime() === s.startTime.getTime();
                    return (
                      <Badge
                        key={`${s.courtId}-${s.startTime.getTime()}`}
                        variant={isSelected ? "default" : "outline"}
                        className="cursor-pointer text-xs gap-1"
                        onClick={() => setSelectedSlot(s)}
                      >
                        <MapPin className="h-2.5 w-2.5" />{s.courtName}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {selectedSlot && oldPrice > 0 && (
          <PriceChangeIndicator
            oldPrice={oldPrice}
            newPrice={newPrice}
            oldTimeLabel={oldTimeLabel}
            newTimeLabel={newTimeLabel}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Avbryt</Button>
          <Button
            disabled={!selectedSlot || rescheduleMutation.isPending}
            onClick={() => rescheduleMutation.mutate()}
          >
            {rescheduleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Flytta hit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
